/**
 * The opt-in and privacy-notice columns: Newsletter, Country, Privacy notice. Used by the
 * classic signups grid (rows carry the whole `waitlist_submission`) and by People (rows
 * carry only the five consent keys, as `waitlist_consent`); each passes its own accessor.
 *
 * Read off `waitlist_submission` by the pure readers in `consentFields.ts`, which also
 * document the five wire keys and why a row without them shows an em-dash rather than "No".
 *
 * Kept OUT of the `WEBSITE_FIELDS` manifest on purpose: that manifest is one column per
 * wire key with a closed set of kinds (number / currency / rate / list / text), and these
 * columns are each composed from more than one key ("Yes (pre-ticked, US)" is three). The
 * keys are still declared once, as `WEBSITE_CONSENT_FIELDS`, beside their readers.
 *
 * Sortable (client-side, over the loaded page, like every other column here) and not
 * filterable: the Filters drawer's string control resolves against `row[field]`, and these
 * values live inside a jsonb blob, so it would offer an unlabelled checkbox that can only
 * empty the table. `websiteColumns.tsx` documents that trap at length.
 */
import React from 'react'

import { Box, Typography } from '@mui/material'
import { EMPTY_VALUE, Tooltip } from '@repo/ui'

import { headerWithInfo, INFO_HEADER_ALLOWANCE } from 'components'
import { date } from 'utils'

import { consentCountryLabel, newsletterLabel, privacyNoticeOf } from './consentFields'

const cellSx = { display: 'flex', alignItems: 'center', height: '100%', minWidth: 0 }

const Empty = () => (
  <Box sx={cellSx}>
    <Typography variant="body2" color="text.disabled">
      {EMPTY_VALUE}
    </Typography>
  </Box>
)

const TextCell = ({ text, title }: { text: string | null; title?: string }) =>
  text === null ? (
    <Empty />
  ) : (
    <Box sx={cellSx}>
      <Tooltip title={title ?? text}>
        <Typography variant="body2" noWrap>
          {text}
        </Typography>
      </Tooltip>
    </Box>
  )

const NEWSLETTER_NOTE =
  'The newsletter box on the request access form. "Pre-ticked" means the box was already ticked when the form opened (the US default) and the visitor left it that way, which is a weaker act than ticking it. The country is the one the site detected from the visitor’s connection, which decided that default. A dash means the submission predates this field.'

const COUNTRY_NOTE =
  'Detected by the site from the visitor’s connection when the form opened, to choose the newsletter default. Approximate, and not something the visitor told us.'

const NOTICE_NOTE =
  'The privacy policy version the form’s privacy notice linked to, and when the site recorded the notice as shown (your local time). The form shows a notice rather than asking for consent. A dash means the submission predates this field.'

/** Where a grid's row keeps the request-access blob (or just its consent keys). */
type ConsentOf<R> = (row: R) => { [key: string]: unknown } | null | undefined

const noticeText = (blob: { [key: string]: unknown } | null | undefined): string | null => {
  const notice = privacyNoticeOf(blob)
  if (!notice) return null
  const at = notice.shownAt ? date(notice.shownAt, 'MMM d yyyy, h:mm a') : null
  return [notice.version ? `v. ${notice.version}` : null, at ? `shown ${at}` : null]
    .filter(Boolean)
    .join(' · ')
}

export const buildConsentColumns = <R,>(consentOf: ConsentOf<R>) => [
  {
    field: 'newsletter_opt_in',
    headerName: 'Newsletter',
    type: 'string',
    width: 190 + INFO_HEADER_ALLOWANCE,
    sortable: true,
    filterable: false,
    renderHeader: headerWithInfo('Newsletter', NEWSLETTER_NOTE),
    valueGetter: (_value: unknown, row: R) => (row ? newsletterLabel(consentOf(row)) : null),
    renderCell: ({ value }: { value: string | null }) => <TextCell text={value ?? null} />,
  },
  {
    field: 'consent_country',
    headerName: 'Country',
    type: 'string',
    width: 170 + INFO_HEADER_ALLOWANCE,
    sortable: true,
    filterable: false,
    renderHeader: headerWithInfo('Country', COUNTRY_NOTE),
    valueGetter: (_value: unknown, row: R) => (row ? consentCountryLabel(consentOf(row)) : null),
    renderCell: ({ value }: { value: string | null }) => <TextCell text={value ?? null} />,
  },
  {
    field: 'privacy_notice',
    headerName: 'Privacy notice',
    type: 'string',
    // Measured in the browser: "v. 6 October 2026 · shown Oct 3 2026, 9:03 AM" renders at
    // 330px; 365 leaves room for a two-digit day and hour without truncating.
    width: 365 + INFO_HEADER_ALLOWANCE,
    sortable: true,
    filterable: false,
    renderHeader: headerWithInfo('Privacy notice', NOTICE_NOTE),
    /*
      Sorts on the raw ISO instant (then the version), not the rendered text: "Oct 10" must
      sort after "Oct 9", which a string sort of "MMM d" does not do.
    */
    valueGetter: (_value: unknown, row: R) => {
      const notice = row ? privacyNoticeOf(consentOf(row)) : null
      return notice ? `${notice.shownAt ?? ''}|${notice.version ?? ''}` : null
    },
    renderCell: ({ row }: { row: R }) => {
      const blob = row ? consentOf(row) : null
      const text = noticeText(blob)
      const raw = privacyNoticeOf(blob)
      return <TextCell text={text} title={raw?.shownAt ? `${text} (${raw.shownAt})` : undefined} />
    },
  },
]
